import {
  getCurrentUser,
  subscribeCurrentUser
} from '../user/current_user.js?v=20260722a';
import { t } from '../i18n/i18n_service.js?v=20260722a';
import { escapeHtml } from '../utils/html_utils.js?v=20260706d';
import {
  getMessageRuntimeState,
  subscribeMessages
} from '../messages/message_runtime.js?v=20260806a';

const renderedHosts = new Set();
let unsubscribeCurrentUser = null;
const actionHandlers = {
  requestLogin: null,
  requestUserProfile: null
};

function safeImageUrl(value) {
  const url = String(value ?? '').trim();
  if (!url) return '';

  const protocolMatch = url.match(/^([a-z][a-z0-9+.-]*):/i);
  if (!protocolMatch) return url;

  const protocol = protocolMatch[1].toLowerCase();
  if (protocol === 'http' || protocol === 'https') return url;
  if (protocol === 'data' && /^data:image\//i.test(url)) return url;
  return '';
}

function userDisplayName(user) {
  const name = `${user?.vorname || ''} ${user?.nachname || ''}`.trim();
  return name || 'Benutzer';
}

function userMetaText(user) {
  return user?.firma || user?.rolle_name || user?.rolle_code || '';
}

function userInitials(user) {
  const first = String(user?.vorname || '').trim().charAt(0);
  const last = String(user?.nachname || '').trim().charAt(0);
  const initials = `${first}${last}`.toUpperCase();
  return initials || '?';
}

function configureSidebarUserCardActions(handlers = {}) {
  if (typeof handlers.requestLogin === 'function') {
    actionHandlers.requestLogin = handlers.requestLogin;
  }
  if (typeof handlers.requestUserProfile === 'function') {
    actionHandlers.requestUserProfile = handlers.requestUserProfile;
  }
}

function requestLogin() {
  if (typeof actionHandlers.requestLogin === 'function') actionHandlers.requestLogin();
}

function requestUserProfile(user) {
  if (typeof actionHandlers.requestUserProfile !== 'function') return;
  // Ungelesene Nachrichten sollen das Profil-Modal direkt im Register
  // "Nachrichten" oeffnen statt immer bei "Eigenes Profil" (Nutzerwunsch
  // 2026-08-18) -- frisch zum Klickzeitpunkt gelesen statt der beim letzten
  // Render erfassten Zahl, damit ein zwischenzeitlich veraendertes
  // Postfach nicht zu einem veralteten Ziel-Tab fuehrt.
  const unreadCount = getMessageRuntimeState().unreadCount;
  actionHandlers.requestUserProfile(user, { initialTab: unreadCount ? 'messages' : undefined });
}

function unreadBadgeHtml(count) {
  const numeric = Math.max(0, Number(count) || 0);
  if (!numeric) return '';
  const label = numeric > 99 ? '99+' : String(numeric);
  return `<span class="sidebar-user-card__message-badge" aria-hidden="true">${label}</span>`;
}

function avatarHtml(user, unreadCount = 0) {
  const imageUrl = safeImageUrl(user?.profilbild_url);
  if (imageUrl) {
    return `
      <span class="sidebar-user-card__avatar" aria-hidden="true">
        <img class="sidebar-user-card__avatar-img" src="${escapeHtml(imageUrl)}" alt="" loading="lazy" decoding="async">
        ${unreadBadgeHtml(unreadCount)}
      </span>
    `;
  }

  return `
    <span class="sidebar-user-card__avatar sidebar-user-card__avatar-fallback" aria-hidden="true">
      ${escapeHtml(userInitials(user))}
      ${unreadBadgeHtml(unreadCount)}
    </span>
  `;
}

function renderAnonymousCard(host) {
  host.innerHTML = `
    <button class="sidebar-user-card sidebar-user-card--anonymous sidebar-user-card__login" type="button">
      ${escapeHtml(t('sidebar_links.benutzerkarte.button.einloggen', 'Einloggen'))}
    </button>
  `;

  host.querySelector('.sidebar-user-card__login')?.addEventListener('click', requestLogin);
}

function renderUserCard(host, user) {
  const meta = userMetaText(user);
  const unreadCount = getMessageRuntimeState().unreadCount;
  const unreadLabel = unreadCount ? `, ${unreadCount > 99 ? 'mehr als 99' : unreadCount} ungelesene Nachrichten` : '';
  host.innerHTML = `
    <button class="sidebar-user-card" type="button" aria-label="Benutzerprofil öffnen${unreadLabel}">
      ${avatarHtml(user, unreadCount)}
      <span class="sidebar-user-card__content">
        <span class="sidebar-user-card__name">${escapeHtml(userDisplayName(user))}</span>
        ${meta ? `<span class="sidebar-user-card__meta">${escapeHtml(meta)}</span>` : ''}
      </span>
    </button>
  `;

  host.querySelector('.sidebar-user-card')?.addEventListener('click', () => requestUserProfile(user));
}

function renderSidebarUserCardContent(host) {
  const user = getCurrentUser();
  if (user) {
    renderUserCard(host, user);
  } else {
    renderAnonymousCard(host);
  }
}

function renderRegisteredHosts() {
  [...renderedHosts].forEach(host => {
    if (!host?.isConnected) {
      renderedHosts.delete(host);
      return;
    }
    renderSidebarUserCardContent(host);
  });
}

function ensureCurrentUserSubscription() {
  if (unsubscribeCurrentUser) return;
  unsubscribeCurrentUser = subscribeCurrentUser(renderRegisteredHosts);
  subscribeMessages(event => {
    if (event.type === 'unread-count' || event.type === 'reset') renderRegisteredHosts();
  });
}

function renderSidebarUserCard(host) {
  if (!host) return false;
  renderedHosts.add(host);
  ensureCurrentUserSubscription();
  renderSidebarUserCardContent(host);
  return true;
}

export {
  configureSidebarUserCardActions,
  renderSidebarUserCard
};
